/* eslint-disable react/prop-types */ // TODO: add props
import { useMemo } from 'react';
import cx from 'classnames';
import PT from 'prop-types';
import IPT from 'react-immutable-proptypes';
import { Link } from 'react-router-dom';
import { QUESTION_GROKKED } from 'edrolo/domains/exams';
import EPT from 'edrolo/propTypes';
import { IndicatorCell, INDICATOR_TYPES } from '../../../../../components';
import {
  RowExpansionArrow,
  ChecklistReflectionSummary,
  ResultsTable,
} from '../../../components';
import { StudentResponseBars } from '../StudentResponseBars';
import { ChecklistBreakdown } from '../ChecklistBreakdown';
import { getProfileQuestionResponseSummary } from '../../../utils';

import style from './WorkedChecklistQuestionReview.scss';

const WorkedChecklistQuestionReview = ({
  question,
  classStudentProfiles,
  responses,
  solution,
  highlightProfileId,
  getStudentCourseProgressUrl,
}) => {
  const profileQuestionResponseSummary = useMemo(
    () =>
      getProfileQuestionResponseSummary(
        classStudentProfiles,
        responses,
        solution
      ),
    [classStudentProfiles, responses, solution]
  );

  const totalMarks = (+question?.get('marks')).toFixed(0);

  const checklistItems = solution
    .get('solution')
    .get('exemplar_response_checklist_items');

  const resultsTableColumns = [
    {
      id: 'name',
      Header: 'Name',
      accessor: (row) => (
        <Link
          to={getStudentCourseProgressUrl(row.get('userProfileId'))}
        >{`${row.get('userFirstName')} ${row.get('userLastName')}`}</Link>
      ),
      sortType: (rowA, rowB) =>
        rowA.original.get('userLastName').toLowerCase() >
        rowB.original.get('userLastName').toLowerCase()
          ? 1
          : -1,
    },
    {
      id: 'studentMark',
      Header: 'Student Mark',
      accessor: (row) => {
        const studentMarks = (+row.getIn([
          'response',
          'text_marks_from_student',
        ])).toFixed(0);
        if (studentMarks && !isNaN(studentMarks)) {
          return `${studentMarks}/${totalMarks}`;
        }

        return 'N/A';
      },
      sortType: (rowA, rowB) =>
        rowA.original.getIn(['response', 'marks_final']) >
        rowB.original.getIn(['response', 'marks_final'])
          ? 1
          : -1,
    },
    {
      id: 'studentResponse',
      style: { width: '50%' },
      Header: () => (
        <div className={style.studentResponseHeader}>Student Response</div>
      ),
      accessor: (row) => {
        return {
          checklistReflections: row.get('checklistReflections'),
        };
      },
      Cell: ({ cell, row }) => {
        const { checklistReflections } = cell.value;
        const responseTextValue = 'See student workbook for response';

        return row.isExpanded ? (
          <div className={style.responseWrapper}>
            <span className={cx(style.fullWidthCell, style.responseTextValue)}>
              {responseTextValue}
            </span>
            <ChecklistReflectionSummary reflections={checklistReflections} />
          </div>
        ) : (
          <div className={style.responseWrapper}>
            <span className={style.responseTextValue}>{responseTextValue}</span>
          </div>
        );
      },
      disableSortBy: true,
    },

    {
      id: 'selfReflection',
      Header: 'Self-reflection',
      accessor: (row) => {
        if (row.get('grokked') === QUESTION_GROKKED.YES) {
          return 'Understands';
        }
        if (row.get('grokked') === QUESTION_GROKKED.NO) {
          return 'Needs help';
        }
        return 'Not reflected';
      },
      Cell: (cell) => {
        if (cell.row.original.get('grokked') === QUESTION_GROKKED.YES) {
          return (
            <IndicatorCell
              value={cell.value}
              indicatorType={INDICATOR_TYPES.POSITIVE}
            />
          );
        }
        if (cell.row.original.get('grokked') === QUESTION_GROKKED.NO) {
          return (
            <IndicatorCell
              value={cell.value}
              indicatorType={INDICATOR_TYPES.NEGATIVE}
            />
          );
        }
        return (
          <IndicatorCell
            value={cell.value}
            indicatorType={INDICATOR_TYPES.NEUTRAL}
          />
        );
      },
      sortType: (rowA, rowB) => {
        const sortOrder = [
          QUESTION_GROKKED.YES,
          QUESTION_GROKKED.NO,
          QUESTION_GROKKED.NO_RESPONSE,
          null,
        ];

        if (
          sortOrder.indexOf(rowA.original.get('grokked')) >
          sortOrder.indexOf(rowB.original.get('grokked'))
        ) {
          return 1;
        }
        return -1;
      },
    },

    {
      id: 'expander',
      Header: '',
      style: { width: '24px' },
      Cell: ({ row }) => (
        <div
          className={style.expanderWidgetContainer}
          {...row.getToggleRowExpandedProps()}
        >
          {row.isExpanded ? (
            <RowExpansionArrow expanded />
          ) : (
            <RowExpansionArrow />
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className={style.container}>
        <div>
          <h3 className={style.heading}>How students responded</h3>
          <StudentResponseBars
            profileQuestionResponseSummary={profileQuestionResponseSummary}
            showCorrectness={false}
          />
        </div>
        <div className={style.checklistBreakdown}>
          <h3 className={style.heading}>Checklist breakdown</h3>
          <ChecklistBreakdown
            profileQuestionResponseSummary={profileQuestionResponseSummary}
            checklistItems={checklistItems}
          />
        </div>
      </div>
      <div>
        <h3 className={style.heading}>Response details</h3>
        <ResultsTable
          data={profileQuestionResponseSummary}
          columns={resultsTableColumns}
          highlightProfileId={highlightProfileId}
        />
      </div>
    </>
  );
};

WorkedChecklistQuestionReview.propTypes = {
  question: EPT.ExamQuestion.isRequired,
  classStudentProfiles: IPT.orderedMapOf(
    IPT.contains({
      user_id: PT.number.isRequired,
      profile_id: PT.number.isRequired,
      first_name: PT.string.isRequired,
      last_name: PT.string.isRequired,
      display_name: PT.string,
      is_active: PT.bool,
    })
  ),
  responses: IPT.listOf(EPT.ExamResponse),
  solution: IPT.contains({
    solution: IPT.contains({
      exemplar_response_checklist_items: IPT.listOf(
        IPT.contains({
          id: PT.number.isRequired,
          description: PT.string.isRequired,
        })
      ),
    }),
  }).isRequired,
  highlightProfileId: PT.number,
  getStudentCourseProgressUrl: PT.func,
};

export default WorkedChecklistQuestionReview;
